import { Fragment } from 'react';
import { string, arrayOf, shape, func, number, bool } from 'prop-types';
import styled, { css } from 'styled-components/macro';
import { hideVisually } from 'polished';

import modularScale from 'styles/helpers/modular-scale';
import orderBy from 'lodash.orderby';
import CountryFlag from 'components/base/CountryFlag';

import { definitionLabel } from 'styles/base-classes';

const Label = styled('span')`
  ${definitionLabel};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.small};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ListLabel = styled(Label)`
  margin: ${({ theme }) =>
    `0 ${theme.spacing.medium} 0 ${theme.spacing.medium}`};

  @media screen and (min-width: 36em) {
    margin: ${({ theme }) =>
      `0 ${theme.spacing.xlarge} 0 ${theme.spacing.xlarge}`};
  }
`;

const List = styled.ul`
  padding: ${({ theme }) => `${theme.spacing.medium} 0`};
`;

const RadioInput = styled.input`
  /** Hide the input field */
  ${hideVisually()};

  ${({ checked }) =>
    checked &&
    css`
      + label {
        span {
          font-weight: 600;
        }
        abbr {
          color: ${({ theme }) => theme.NightBlue};
        }
      }
    `}
`;

const CountriesLabel = styled.label`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  background: ${({ theme, isSelected }) => (isSelected ? theme.Sand : 'none')};

  @media screen and (min-width: 36em) {
    padding-left: ${({ theme }) => theme.spacing.large};
    padding-right: ${({ theme }) => theme.spacing.large};
  }
`;

const CountryID = styled.abbr`
  flex: 0 0 2em;
  font-size: ${modularScale(-1)};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.CasperGrey};
  line-height: 1.2;
  font-weight: 600;
  padding-left: ${({ theme }) => theme.spacing.small};
  margin-left: auto;
  text-decoration: none;
`;

const NoCountriesAvailable = styled.li`
  text-align: center;
  padding: ${({ theme }) =>
    `0 ${theme.spacing.small} ${theme.spacing.xxsmall}`};
  font-size: ${modularScale(-1)};

  @media screen and (min-width: 36em) {
    padding-left: ${({ theme }) => theme.spacing.large};
    padding-right: ${({ theme }) => theme.spacing.large};
  }
`;

/** Displays a list of countries */
const CountryList = ({
  handleChange,
  countries,
  countryFilter,
  sortByKey = 'displayName',
}) => {
  const labelText = 'All countries';

  if (countries.length === 0) {
    return null;
  }

  const sortedCountries = orderBy(
    countries,
    ['isDisabled', sortByKey],
    ['asc', sortByKey === 'lastUsed' ? 'desc' : 'asc']
  );

  return (
    <>
      <ListLabel>{labelText}</ListLabel>

      <List>
        {sortedCountries.every((country) => country.isDisabled) && (
          <NoCountriesAvailable>
            There are no more countries available to compare
          </NoCountriesAvailable>
        )}

        {sortedCountries.map((country) => {
          const itemId = `countries-list--${country.id}`;

          return (
            <Fragment key={itemId}>
              <li>
                <RadioInput
                  type="radio"
                  name="country"
                  id={itemId}
                  value={country.id}
                  onChange={handleChange}
                  checked={countryFilter ? countryFilter === country.id : false}
                  disabled={country.isDisabled}
                />
                <CountriesLabel
                  htmlFor={itemId}
                  isSelected={countryFilter && countryFilter === country.id}
                  isDisabled={country.isDisabled}
                >
                  <CountryFlag {...country} small />
                  <span>{country.displayName}</span>
                  <CountryID title={country.displayName}>
                    {country.id}
                  </CountryID>
                </CountriesLabel>
              </li>
            </Fragment>
          );
        })}
      </List>
    </>
  );
};

CountryList.propTypes = {
  /** Method that is called when a country is clicked */
  handleChange: func.isRequired,
  /** The (filtered) list of countries */
  countries: arrayOf(
    shape({
      id: string.isRequired,
      displayName: string.isRequired,
      flagImgLarge: string,
      flagImgMedium: string,
      flagImgSmall: string,
      lat: number,
      lon: number,
    })
  ).isRequired,
  /** The active country */
  countryFilter: string,
  /** Key to sort countries by */
  sortByKey: string,
  /** Boolean indicating if this shows recently used countries */
  isRecentFiltersPanel: bool,
};

export default CountryList;
